import { useEffect, useMemo, useRef, useState } from "react";
import {
  Layers,
  Activity,
  Flame,
  Crosshair,
  Bone,
  Brain,
  Eye,
  EyeOff,
  RotateCw,
  Info,
  Sparkles,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import {
  MUSCLE_CATALOG,
  MUSCLE_BY_ID,
  REGION_LABELS,
  canonicalMuscleId,
} from "../../data/muscleAtlas";
import { SKELETAL_LANDMARKS, LANDMARKS_BY_VIEW } from "../../data/skeletalLandmarks";
import { bodyStageGeometry } from "../../data/bodyAtlasBounds";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * Iter X — AnatomyViewer (top-notch realistic anatomy surface).
 *
 *   • Realistic atlas-quality base image (anterior/posterior, M/F).
 *   • Toggleable overlay layers stacked on top of the base:
 *       - Treatment History (heatmap derived from sessions.worked_areas)
 *       - Trigger Points (clinical dots with type tier)
 *       - Referral Patterns (halos on selected/hovered muscles)
 *       - Skeletal Landmarks (pinned bony references)
 *       - Clinical Insights (AI-anchored highlights — wires into future endpoint)
 *   • Single-view-at-a-time on mobile, flip toggle to switch sides.
 *   • Tapping a trigger-point dot or muscle pin opens a structured detail panel.
 *
 * This component is intentionally read-mostly. It is wired into the Body Chart
 * History page first (read-only visualization). The "selection" use case
 * (tap-to-toggle worked muscles during a session) still uses the legacy
 * BodyDiagram so we can ship without disturbing the New Session workflow.
 */

const TAB_DEFS = [
  { id: "labels", label: "Labels", icon: Eye },
  { id: "triggerPoints", label: "Trigger Points", icon: Crosshair },
  { id: "referral", label: "Referral Patterns", icon: Activity },
  { id: "landmarks", label: "Skeletal Landmarks", icon: Bone },
  { id: "insights", label: "Clinical Insights", icon: Brain, badge: "AI" },
];

const TRIGGER_COLOR = {
  common: "#C53D2E",
  secondary: "#D49B2E",
  possible: "#9C8A6E",
};

/**
 * Picks which atlas view to show by default. When a heatmap is supplied, we
 * default to whichever side has more total session-work — so clients opening
 * a portal whose history is all posterior (neck/lat work) don't see an
 * "empty" anterior figure on first load.
 */
function pickInitialView(heatmap, fallback) {
  if (!heatmap) return fallback;
  let anterior = 0, posterior = 0;
  for (const [mid, h] of Object.entries(heatmap)) {
    const m = MUSCLE_BY_ID[mid] || MUSCLE_BY_ID[canonicalMuscleId(mid)];
    if (!m) continue;
    const count = (h?.count || 0);
    if ((m.visibleOn || []).includes("anterior")) anterior += count;
    if ((m.visibleOn || []).includes("posterior")) posterior += count;
  }
  if (anterior === 0 && posterior > 0) return "posterior";
  if (posterior === 0 && anterior > 0) return "anterior";
  return fallback;
}

export default function AnatomyViewer({
  heatmap = null,
  selectedMuscleId = null,
  onMuscleSelect = () => {},
  sex = "m",
  defaultView = "anterior",
  testIdPrefix = "anatomy-viewer",
  clientId = null,         // optional — enables Clinical Insights tab
  sessionId = null,        // optional — enables per-session cached insights
  activeTab: controlledActiveTab = null,   // optional — when set, drives the active tab from outside
  onActiveTabChange = null,                // optional — fires when the user clicks a tab
  showTabStrip = true,                     // hide the tab strip when controlled externally
}) {
  const initialView = useMemo(
    () => pickInitialView(heatmap, defaultView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [heatmap ? Object.keys(heatmap).sort().join(",") : "", defaultView]
  );
  const [view, setView] = useState(initialView); // anterior | posterior
  useEffect(() => { setView(initialView); }, [initialView]);
  const [internalActiveTab, setInternalActiveTab] = useState("labels");
  const activeTab = controlledActiveTab || internalActiveTab;
  const setActiveTab = (next) => {
    if (onActiveTabChange) onActiveTabChange(next);
    if (!controlledActiveTab) setInternalActiveTab(next);
  };
  // Multi-select for Clinical Insights tab. Driven by the heatmap by default
  // (so the AI starts with the "most worked" muscles), with click-to-toggle.
  const [insightSelection, setInsightSelection] = useState(() =>
    heatmap ? Object.keys(heatmap).slice(0, 6) : (selectedMuscleId ? [selectedMuscleId] : [])
  );
  const [hoverMuscleId, setHoverMuscleId] = useState(null);

  const activeMuscle = useMemo(
    () => (selectedMuscleId ? MUSCLE_BY_ID[selectedMuscleId] : null),
    [selectedMuscleId]
  );

  // Atlas image — keyed off (sex, view); defaults to male if F figure missing
  const figureSrc = `/anatomy/atlas/${view}_${sex || "m"}.png`;

  // Muscles visible on the current view (used for trigger-point dots + halos)
  const visibleMuscles = useMemo(
    () => MUSCLE_CATALOG.filter((m) => (m.visibleOn || []).includes(view)),
    [view]
  );

  // Heatmap intensity by canonical muscle id (only those on current view)
  const heatRows = useMemo(() => {
    if (!heatmap) return [];
    const rows = [];
    for (const m of visibleMuscles) {
      const h = heatmap[m.id] || heatmap[canonicalMuscleId(m.id)];
      if (h) rows.push({ muscle: m, heat: h });
    }
    return rows;
  }, [heatmap, visibleMuscles]);

  // Map tab → which overlays to render (treatment history is always on)
  const overlays = {
    showHistory: true,
    showTriggerPoints: activeTab === "triggerPoints",
    showReferral: activeTab === "referral",
    showLandmarks: activeTab === "landmarks",
    showInsights: activeTab === "insights",
  };

  const toggleInsightMuscle = (mid) => {
    setInsightSelection((cur) =>
      cur.includes(mid) ? cur.filter((x) => x !== mid) : [...cur, mid].slice(0, 12)
    );
  };

  return (
    <div className="space-y-4" data-testid={testIdPrefix}>
      {/* Tab strip + view flip */}
      <div className="flex flex-wrap items-center gap-3" data-testid={`${testIdPrefix}-controls`}>
        <ViewFlipToggle view={view} setView={setView} testid={`${testIdPrefix}-flip`} />
        <div className="flex-1 min-w-0" />
        {showTabStrip && (
        <div
          className="inline-flex flex-wrap gap-1 bg-paper-100 rounded-2xl p-1"
          role="tablist"
          aria-label="Anatomy layers"
          data-testid={`${testIdPrefix}-tabs`}
        >
          {TAB_DEFS.map((t) => {
            const Icon = t.icon;
            const on = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setActiveTab(t.id)}
                data-testid={`${testIdPrefix}-tab-${t.id}`}
                data-active={on}
                className={`min-h-[40px] px-3 sm:px-4 rounded-xl text-xs sm:text-sm font-medium inline-flex items-center gap-1.5 transition-all ${
                  on
                    ? "bg-white text-ink-900 shadow-sm"
                    : "text-ink-600 hover:text-ink-900"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden">{t.label.split(" ")[0]}</span>
                {t.badge && (
                  <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${on ? "bg-sage-500 text-white" : "bg-ink-900/10 text-ink-700"}`}>{t.badge}</span>
                )}
              </button>
            );
          })}
        </div>
        )}
      </div>

      {/* Stage + side panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-6">
        <AnatomyStage
          figureSrc={figureSrc}
          view={view}
          sex={sex}
          heatRows={heatRows}
          {...overlays}
          visibleMuscles={visibleMuscles}
          hoverMuscleId={hoverMuscleId}
          setHoverMuscleId={setHoverMuscleId}
          selectedMuscleId={selectedMuscleId}
          onMuscleSelect={onMuscleSelect}
          testIdPrefix={testIdPrefix}
          insightSelection={insightSelection}
          activeTab={activeTab}
        />

        <MuscleDetailPanel
          muscle={activeMuscle || (hoverMuscleId ? MUSCLE_BY_ID[hoverMuscleId] : null)}
          heat={
            (activeMuscle || hoverMuscleId)
              ? heatmap?.[(activeMuscle || MUSCLE_BY_ID[hoverMuscleId])?.id]
              : null
          }
          testIdPrefix={testIdPrefix}
        />
      </div>

      {/* Education panel — content driven by the active tab */}
      <TabEducationPanel
        tab={activeTab}
        view={view}
        visibleMuscles={visibleMuscles}
        selectedMuscleId={selectedMuscleId}
        onMuscleSelect={onMuscleSelect}
        heatmap={heatmap}
        clientId={clientId}
        sessionId={sessionId}
        insightSelection={insightSelection}
        toggleInsightMuscle={toggleInsightMuscle}
        setInsightSelection={setInsightSelection}
        testIdPrefix={testIdPrefix}
      />
    </div>
  );
}

// ── View flip toggle ───────────────────────────────────────────────────
function ViewFlipToggle({ view, setView, testid }) {
  return (
    <div className="inline-flex items-center bg-paper-100 rounded-full p-1" data-testid={testid}>
      <FlipBtn
        active={view === "anterior"}
        onClick={() => setView("anterior")}
        testid={`${testid}-anterior`}
        label="Front"
      />
      <FlipBtn
        active={view === "posterior"}
        onClick={() => setView("posterior")}
        testid={`${testid}-posterior`}
        label="Back"
      />
      <button
        type="button"
        onClick={() => setView(view === "anterior" ? "posterior" : "anterior")}
        data-testid={`${testid}-swap`}
        aria-label="Flip view"
        className="ml-1 min-h-[32px] px-2.5 rounded-full text-ink-500 hover:text-ink-900"
      >
        <RotateCw className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function FlipBtn({ active, onClick, testid, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`min-h-[32px] px-3 rounded-full text-xs font-medium transition-colors ${
        active ? "bg-white text-ink-900 shadow-sm" : "text-ink-600 hover:text-ink-900"
      }`}
    >
      {label}
    </button>
  );
}

// ── Anatomy stage (image + SVG overlays) ────────────────────────────────
function AnatomyStage({
  figureSrc,
  view,
  sex,
  heatRows,
  showHistory,
  showTriggerPoints,
  showReferral,
  showLandmarks,
  showInsights,
  visibleMuscles,
  hoverMuscleId,
  setHoverMuscleId,
  selectedMuscleId,
  onMuscleSelect,
  testIdPrefix,
  insightSelection,
  activeTab,
}) {
  const geom = bodyStageGeometry(view, sex || "m");
  return (
    <div
      className="relative mx-auto h-full"
      style={{
        // Hero sizing: body dominates the screen on mobile + portrait tablet.
        maxHeight: "min(70vh, 760px)",
        height: "min(60vh, 680px)",
      }}
      data-testid={`${testIdPrefix}-stage-wrap`}
    >
      <div
        className="relative mx-auto h-full"
        style={{ aspectRatio: geom.aspect }}
        data-view={view}
        data-testid={`${testIdPrefix}-stage`}
      >
        <div
          aria-hidden
          className="absolute -inset-4 sm:-inset-6 rounded-[40px] pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 50% 35%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 70%)",
            filter: "blur(12px)",
          }}
        />
        <div
          className="relative w-full h-full overflow-hidden rounded-3xl"
          style={{
            background: "radial-gradient(ellipse at 50% 30%, #FBF8F1 0%, #F4EFE3 60%, #ECE5D2 100%)",
          }}
        >
          <img
            src={figureSrc}
            alt={`Anatomical ${view} view`}
            style={geom.imgStyle}
            draggable="false"
          />

      {/* SVG overlay layer — viewBox 0..100 along both axes so all percentages map straight in */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden="false"
        role="img"
      >
        {/* Treatment history heatmap — soft tinted circles on each worked muscle (per side for bilateral) */}
        {showHistory && heatRows.map(({ muscle, heat }) => {
          const dots = muscleAnchorsOnView(muscle, view);
          if (!dots.length) return null;
          const trend = heat.trend || "unknown";
          const fill =
            trend === "improving"
              ? "rgba(131, 146, 107, 0.55)"
              : trend === "worse"
              ? "rgba(200, 85, 61, 0.6)"
              : trend === "stable"
              ? "rgba(212, 160, 91, 0.55)"
              : "rgba(140, 122, 96, 0.45)";
          const radius = 2.2 + 1.8 * Math.max(0.2, Math.min(1, heat.intensity || 0.3));
          return (
            <g
              key={`heat-${muscle.id}`}
              data-testid={`${testIdPrefix}-heat-${muscle.id}`}
              onClick={() => onMuscleSelect(muscle.id)}
              style={{ cursor: "pointer" }}
            >
              {dots.map((dot, i) => (
                <g key={i}>
                  <circle cx={dot.x} cy={dot.y} r={radius} fill={fill} stroke="#5A4F3F" strokeWidth="0.25" opacity="0.85" />
                  {heat.count >= 3 && (
                    <circle cx={dot.x} cy={dot.y} r={radius + 1.2} fill="none" stroke="#9C4231" strokeWidth="0.35" strokeDasharray="0.6 0.8" opacity="0.7" />
                  )}
                </g>
              ))}
            </g>
          );
        })}

        {/* Referral halo — only on hovered/selected muscle when layer is on */}
        {showReferral && (selectedMuscleId || hoverMuscleId) && (() => {
          const id = selectedMuscleId || hoverMuscleId;
          const m = MUSCLE_BY_ID[id];
          if (!m) return null;
          const dot = muscleAnchorOnView(m, view);
          if (!dot) return null;
          return (
            <g data-testid={`${testIdPrefix}-referral-${m.id}`}>
              <defs>
                <radialGradient id={`ref-grad-${m.id}`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#9C4231" stopOpacity="0.55" />
                  <stop offset="60%" stopColor="#9C4231" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#9C4231" stopOpacity="0" />
                </radialGradient>
              </defs>
              <circle cx={dot.x} cy={dot.y} r="10" fill={`url(#ref-grad-${m.id})`} />
            </g>
          );
        })()}

        {/* Trigger points layer */}
        {showTriggerPoints && visibleMuscles.map((m) =>
          (m.triggerPoints || [])
            .filter((tp) => tp.position?.view === view)
            .map((tp, i) => (
              <g
                key={`tp-${m.id}-${i}`}
                data-testid={`${testIdPrefix}-tp-${m.id}-${i}`}
                onClick={() => onMuscleSelect(m.id)}
                onMouseEnter={() => setHoverMuscleId(m.id)}
                onMouseLeave={() => setHoverMuscleId(null)}
                style={{ cursor: "pointer" }}
              >
                <circle
                  cx={tp.position.x}
                  cy={tp.position.y}
                  r={tp.type === "common" ? 1.4 : tp.type === "secondary" ? 1.1 : 0.9}
                  fill={TRIGGER_COLOR[tp.type] || TRIGGER_COLOR.common}
                  stroke="#FAF7F0"
                  strokeWidth="0.35"
                />
                <circle
                  cx={tp.position.x}
                  cy={tp.position.y}
                  r={(tp.type === "common" ? 1.4 : 1.1) + 1.2}
                  fill="none"
                  stroke={TRIGGER_COLOR[tp.type] || TRIGGER_COLOR.common}
                  strokeWidth="0.3"
                  opacity="0.35"
                />
              </g>
            ))
        )}

        {/* Skeletal landmarks — bony references with labels on hover */}
        {showLandmarks && (LANDMARKS_BY_VIEW[view] || []).map((lm) => (
          <g
            key={`lm-${lm.id}`}
            data-testid={`${testIdPrefix}-landmark-${lm.id}`}
          >
            <circle cx={lm.x} cy={lm.y} r="1.3" fill="#FAF7F0" stroke="#5A4F3F" strokeWidth="0.4" />
            <circle cx={lm.x} cy={lm.y} r="2.6" fill="none" stroke="#5A4F3F" strokeWidth="0.25" opacity="0.45" />
          </g>
        ))}

        {/* Insight selection overlay — rings around all muscles the therapist has staged for AI insights */}
        {showInsights && insightSelection && insightSelection.length > 0 && insightSelection.map((mid) => {
          const m = MUSCLE_BY_ID[mid] || MUSCLE_BY_ID[canonicalMuscleId(mid)];
          if (!m) return null;
          const dots = muscleAnchorsOnView(m, view);
          if (!dots.length) return null;
          return (
            <g
              key={`insight-sel-${mid}`}
              data-testid={`${testIdPrefix}-insight-sel-${mid}`}
              onClick={() => onMuscleSelect(mid)}
              style={{ cursor: "pointer" }}
            >
              {dots.map((dot, i) => (
                <circle key={i} cx={dot.x} cy={dot.y} r="3.2" fill="rgba(125, 91, 166, 0.18)" stroke="#7D5BA6" strokeWidth="0.45" strokeDasharray="0.8 0.5" />
              ))}
            </g>
          );
        })}

        {/* Clinical insights — placeholder: lights selected muscle anchor in purple */}
        {showInsights && (selectedMuscleId || hoverMuscleId) && (() => {
          const id = selectedMuscleId || hoverMuscleId;
          const m = MUSCLE_BY_ID[id];
          if (!m) return null;
          const dot = muscleAnchorOnView(m, view);
          if (!dot) return null;
          return (
            <g data-testid={`${testIdPrefix}-insight-${m.id}`}>
              <circle cx={dot.x} cy={dot.y} r="4.5" fill="rgba(120, 90, 160, 0.18)" stroke="#7D5BA6" strokeWidth="0.4" strokeDasharray="0.8 0.6" />
            </g>
          );
        })()}
      </svg>

      {/* Hover hint chip */}
      {hoverMuscleId && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-ink-900/90 text-white text-xs whitespace-nowrap pointer-events-none"
          data-testid={`${testIdPrefix}-hover-chip`}
        >
          {MUSCLE_BY_ID[hoverMuscleId]?.label}
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

// Anchor coordinate(s) of a muscle on a given view.
//   • For bilateral muscles (trigger points on both sides), returns up to TWO
//     anchors — one per side — so heat dots land on the actual muscle bellies
//     instead of on the centerline (spine/sternum).
//   • For unilateral or centrally located muscles, returns one anchor.
function muscleAnchorsOnView(muscle, view) {
  const onView = (muscle.triggerPoints || []).filter((tp) => tp.position?.view === view);
  if (onView.length === 0) return [];
  const left = onView.filter((tp) => tp.position.x < 50);
  const right = onView.filter((tp) => tp.position.x >= 50);
  const centroid = (arr) => ({
    x: arr.reduce((s, t) => s + t.position.x, 0) / arr.length,
    y: arr.reduce((s, t) => s + t.position.y, 0) / arr.length,
  });
  const anchors = [];
  if (left.length) anchors.push(centroid(left));
  if (right.length) anchors.push(centroid(right));
  return anchors;
}

// Legacy single-anchor helper kept for referral/insight halos that follow
// the hovered/selected muscle — uses the first available side.
function muscleAnchorOnView(muscle, view) {
  const all = muscleAnchorsOnView(muscle, view);
  return all[0] || null;
}

// ── Muscle detail side panel ───────────────────────────────────────────
function MuscleDetailPanel({ muscle, heat, testIdPrefix }) {
  if (!muscle) {
    return (
      <aside
        className="bg-paper-50 border border-paper-200 rounded-3xl p-6 lg:sticky lg:top-4 h-fit"
        data-testid={`${testIdPrefix}-detail-empty`}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-white border border-paper-200 flex items-center justify-center shrink-0">
            <Info className="w-4 h-4 text-ink-500" />
          </div>
          <div>
            <div className="label-overline text-ink-500 mb-1">Tap a muscle, dot, or pin</div>
            <p className="text-sm text-ink-700">
              Anatomy, common complaints, trigger points, referral patterns, orthopedic tests, and treatment history will appear here.
            </p>
          </div>
        </div>
      </aside>
    );
  }

  const region = REGION_LABELS[muscle.region] || muscle.region;

  return (
    <aside
      className="bg-white border border-paper-200 rounded-3xl p-5 lg:sticky lg:top-4 h-fit"
      data-testid={`${testIdPrefix}-detail`}
    >
      <div className="mb-3">
        <div className="label-overline text-sage-600 mb-1">{region}</div>
        <h3 className="font-heading text-xl text-ink-900" data-testid={`${testIdPrefix}-detail-label`}>
          {muscle.label}
        </h3>
      </div>

      {heat && (
        <div className="mb-4 bg-terracotta-500/5 border border-terracotta-500/20 rounded-2xl p-3" data-testid={`${testIdPrefix}-detail-heat`}>
          <div className="text-xs text-ink-700">
            Worked <strong>{heat.count}</strong>× across this client&rsquo;s history ·{" "}
            <TrendInline trend={heat.trend} />
          </div>
        </div>
      )}

      <Section title="Anatomy">
        <Row label="Origin" value={muscle.anatomy?.origin} />
        <Row label="Insertion" value={muscle.anatomy?.insertion} />
        <Row label="Action" value={muscle.anatomy?.action} />
        <Row label="Innervation" value={muscle.anatomy?.innervation} />
      </Section>

      {muscle.clinical?.commonComplaints?.length > 0 && (
        <Section title="Common complaints">
          <ul className="list-disc pl-5 text-sm text-ink-900 space-y-1">
            {muscle.clinical.commonComplaints.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </Section>
      )}

      {muscle.referralPattern && (
        <Section title="Referral pattern">
          <p className="text-sm text-ink-700 leading-relaxed">{muscle.referralPattern}</p>
        </Section>
      )}

      {muscle.triggerPoints?.length > 0 && (
        <Section title="Trigger points">
          <ul className="space-y-2 text-sm">
            {muscle.triggerPoints.map((tp, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  className="w-2 h-2 mt-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: TRIGGER_COLOR[tp.type] }}
                />
                <span className="text-ink-700">
                  <span className="font-medium text-ink-900 capitalize">{tp.type}</span>: {tp.referralPattern}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {muscle.clinical?.orthopedicTests?.length > 0 && (
        <Section title="Orthopedic tests">
          <div className="flex flex-wrap gap-1.5">
            {muscle.clinical.orthopedicTests.map((t) => (
              <span key={t} className="text-[11px] uppercase tracking-[0.1em] bg-paper-100 px-2 py-1 rounded-full text-ink-700">
                {t}
              </span>
            ))}
          </div>
        </Section>
      )}

      {muscle.clinical?.frequentlyWorksWith?.length > 0 && (
        <Section title="Frequently works with">
          <div className="flex flex-wrap gap-1.5">
            {muscle.clinical.frequentlyWorksWith.map((id) => {
              const m = MUSCLE_BY_ID[id];
              return (
                <span key={id} className="text-xs bg-sage-500/10 text-sage-700 px-2 py-1 rounded-full">
                  {m ? m.label : id}
                </span>
              );
            })}
          </div>
        </Section>
      )}
    </aside>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="label-overline mb-2 text-ink-500">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-sm mb-1.5">
      <span className="font-medium text-ink-900">{label}: </span>
      <span className="text-ink-700">{value}</span>
    </div>
  );
}

function TrendInline({ trend }) {
  if (!trend || trend === "unknown") return <span className="text-ink-500">trend pending</span>;
  if (trend === "improving") return <span className="text-sage-700 font-medium">improving</span>;
  if (trend === "worse") return <span className="text-terracotta-700 font-medium">worsening</span>;
  return <span className="text-amber-700 font-medium">holding steady</span>;
}


// ── Bottom education panel — switches by active tab ────────────────────
function TabEducationPanel({
  tab,
  view,
  visibleMuscles,
  selectedMuscleId,
  onMuscleSelect,
  heatmap,
  clientId,
  sessionId,
  insightSelection,
  toggleInsightMuscle,
  setInsightSelection,
  testIdPrefix,
}) {
  return (
    <div
      className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6"
      data-testid={`${testIdPrefix}-edu`}
      data-tab={tab}
    >
      {tab === "labels" && (
        <LabelsPanel
          visibleMuscles={visibleMuscles}
          selectedMuscleId={selectedMuscleId}
          onMuscleSelect={onMuscleSelect}
          heatmap={heatmap}
          testIdPrefix={testIdPrefix}
        />
      )}
      {tab === "triggerPoints" && (
        <TriggerPointsPanel
          visibleMuscles={visibleMuscles}
          view={view}
          onMuscleSelect={onMuscleSelect}
          testIdPrefix={testIdPrefix}
        />
      )}
      {tab === "referral" && (
        <ReferralPanel
          visibleMuscles={visibleMuscles}
          selectedMuscleId={selectedMuscleId}
          onMuscleSelect={onMuscleSelect}
          testIdPrefix={testIdPrefix}
        />
      )}
      {tab === "landmarks" && (
        <LandmarksPanel view={view} testIdPrefix={testIdPrefix} />
      )}
      {tab === "insights" && (
        <InsightsPanel
          clientId={clientId}
          sessionId={sessionId}
          view={view}
          visibleMuscles={visibleMuscles}
          insightSelection={insightSelection}
          toggleInsightMuscle={toggleInsightMuscle}
          setInsightSelection={setInsightSelection}
          heatmap={heatmap}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
}

function PanelHeader({ icon: Icon, label, subtitle, right }) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-paper-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-ink-700" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-lg text-ink-900">{label}</div>
        {subtitle && <p className="text-xs text-ink-700 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function LabelsPanel({ visibleMuscles, selectedMuscleId, onMuscleSelect, heatmap, testIdPrefix }) {
  // Group by region
  const byRegion = useMemo(() => {
    const m = {};
    for (const x of visibleMuscles) (m[x.region] = m[x.region] || []).push(x);
    return m;
  }, [visibleMuscles]);
  return (
    <div data-testid={`${testIdPrefix}-edu-labels`}>
      <PanelHeader icon={Eye} label="Anatomy labels" subtitle="Every muscle visible on this view, grouped by region. Tap a label to focus the muscle panel." />
      <div className="space-y-4">
        {Object.entries(byRegion).map(([region, muscles]) => (
          <div key={region}>
            <div className="label-overline text-sage-600 mb-2">{REGION_LABELS[region] || region}</div>
            <div className="flex flex-wrap gap-1.5">
              {muscles.map((m) => {
                const isActive = m.id === selectedMuscleId;
                const hot = heatmap?.[m.id];
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => onMuscleSelect(m.id)}
                    data-testid={`${testIdPrefix}-label-chip-${m.id}`}
                    className={`min-h-[32px] px-3 rounded-full text-xs font-medium border transition-colors ${
                      isActive
                        ? "bg-ink-900 text-white border-ink-900"
                        : "bg-white text-ink-700 border-paper-200 hover:border-ink-900/30"
                    }`}
                  >
                    {m.label}
                    {hot?.count >= 1 && (
                      <span className={`ml-1.5 inline-block min-w-[18px] px-1 rounded-full text-[10px] font-semibold ${isActive ? "bg-white/20 text-white" : "bg-terracotta-500/15 text-terracotta-700"}`}>
                        {hot.count}×
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TriggerPointsPanel({ visibleMuscles, view, onMuscleSelect, testIdPrefix }) {
  const rows = useMemo(() => {
    const out = [];
    for (const m of visibleMuscles) {
      const tps = (m.triggerPoints || []).filter((tp) => tp.position?.view === view);
      if (tps.length === 0) continue;
      out.push({ muscle: m, tps });
    }
    return out;
  }, [visibleMuscles, view]);
  return (
    <div data-testid={`${testIdPrefix}-edu-triggerPoints`}>
      <PanelHeader
        icon={Crosshair}
        label="Trigger points"
        subtitle={`${rows.length} muscle${rows.length === 1 ? "" : "s"} with documented trigger points on this view. Red = common, amber = secondary, beige = possible.`}
      />
      <div className="space-y-3">
        {rows.map(({ muscle, tps }) => (
          <button
            key={muscle.id}
            type="button"
            onClick={() => onMuscleSelect(muscle.id)}
            data-testid={`${testIdPrefix}-tp-row-${muscle.id}`}
            className="w-full text-left rounded-2xl bg-paper-50 hover:bg-white border border-paper-200 hover:border-ink-900/20 p-4 transition-colors"
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="font-medium text-ink-900">{muscle.label}</div>
              <div className="text-xs text-ink-500">{REGION_LABELS[muscle.region] || muscle.region}</div>
            </div>
            <ul className="space-y-1.5">
              {tps.map((tp, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="w-2 h-2 mt-1.5 rounded-full shrink-0" style={{ backgroundColor: TRIGGER_COLOR[tp.type] }} />
                  <span className="text-ink-700">
                    <span className="font-medium text-ink-900 capitalize">{tp.type}</span>: {tp.referralPattern}
                  </span>
                </li>
              ))}
            </ul>
          </button>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-ink-500">No documented trigger points for muscles on this view. Flip to the other view to see more.</p>
        )}
      </div>
    </div>
  );
}

function ReferralPanel({ visibleMuscles, selectedMuscleId, onMuscleSelect, testIdPrefix }) {
  const withPatterns = visibleMuscles.filter((m) => m.referralPattern);
  return (
    <div data-testid={`${testIdPrefix}-edu-referral`}>
      <PanelHeader
        icon={Activity}
        label="Referral patterns"
        subtitle="Where pain travels from each muscle. Useful when the client's symptom doesn't match the source."
      />
      <div className="space-y-3">
        {withPatterns.map((m) => {
          const isActive = m.id === selectedMuscleId;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onMuscleSelect(m.id)}
              data-testid={`${testIdPrefix}-referral-row-${m.id}`}
              className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                isActive ? "bg-terracotta-500/5 border-terracotta-500/40" : "bg-paper-50 border-paper-200 hover:border-ink-900/20"
              }`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="font-medium text-ink-900">{m.label}</div>
                <div className="text-[11px] uppercase tracking-[0.1em] text-ink-500">{REGION_LABELS[m.region] || m.region}</div>
              </div>
              <p className="text-sm text-ink-700 leading-relaxed">{m.referralPattern}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LandmarksPanel({ view, testIdPrefix }) {
  const items = LANDMARKS_BY_VIEW[view] || [];
  return (
    <div data-testid={`${testIdPrefix}-edu-landmarks`}>
      <PanelHeader
        icon={Bone}
        label="Skeletal landmarks"
        subtitle="Bony references for palpation and orientation."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((lm) => (
          <div
            key={lm.id}
            data-testid={`${testIdPrefix}-landmark-row-${lm.id}`}
            className="rounded-2xl bg-paper-50 border border-paper-200 p-4"
          >
            <div className="font-medium text-ink-900 mb-1">{lm.label}</div>
            <div className="text-xs text-ink-500 mb-1.5"><span className="font-medium">Palpation:</span> {lm.palpation}</div>
            <div className="text-xs text-ink-700"><span className="font-medium">Clinical:</span> {lm.clinical}</div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-ink-500">No skeletal landmarks documented for this view.</p>
        )}
      </div>
    </div>
  );
}

function InsightsPanel({
  clientId, sessionId, view,
  visibleMuscles, insightSelection, toggleInsightMuscle, setInsightSelection, heatmap, testIdPrefix,
}) {
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState("");
  const [question, setQuestion] = useState("");

  const run = async (force = false) => {
    if (!clientId) { setError("Open this from a client's body chart to use Clinical Insights."); return; }
    if (insightSelection.length === 0) { setError("Pick at least one muscle below."); return; }
    setBusy(true); setError("");
    try {
      const r = await api.post("/clinical-insights", {
        client_id: clientId,
        session_id: sessionId || null,
        muscle_ids: insightSelection,
        view,
        question: question.trim() || null,
        force_regenerate: force,
      });
      setInsights(r.data);
      if (r.data.cached) toast.info("Showing cached insights — click Regenerate to refresh.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate insights.");
    } finally { setBusy(false); }
  };

  // Reset cached display when selection changes meaningfully
  useEffect(() => { setInsights(null); }, [insightSelection.join(","), question]);

  return (
    <div data-testid={`${testIdPrefix}-edu-insights`}>
      <PanelHeader
        icon={Brain}
        label="Clinical Insights"
        subtitle="AI clinical reasoning anchored to this client's history. Pick the muscles you want explained, add an optional question, then generate."
        right={<span className="text-[10px] uppercase tracking-wider bg-sage-500/15 text-sage-800 px-2 py-1 rounded-full font-semibold">AI</span>}
      />

      {/* Selected muscles staging */}
      <div className="mb-4">
        <div className="label-overline mb-2 flex items-center justify-between">
          <span>Selected for insight ({insightSelection.length})</span>
          {insightSelection.length > 0 && (
            <button
              type="button"
              onClick={() => setInsightSelection([])}
              data-testid={`${testIdPrefix}-insights-clear`}
              className="text-[11px] uppercase tracking-wider text-ink-500 hover:text-ink-900"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 min-h-[36px]">
          {insightSelection.length === 0 && (
            <p className="text-xs text-ink-500">Tap muscles in the picker below to stage them.</p>
          )}
          {insightSelection.map((mid) => {
            const m = MUSCLE_BY_ID[mid] || MUSCLE_BY_ID[canonicalMuscleId(mid)];
            return (
              <button
                key={mid}
                type="button"
                onClick={() => toggleInsightMuscle(mid)}
                data-testid={`${testIdPrefix}-insights-sel-${mid}`}
                className="min-h-[32px] px-3 rounded-full text-xs font-medium bg-ink-900 text-white inline-flex items-center gap-1.5"
              >
                {m?.label || mid}
                <span className="text-white/70">×</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Picker */}
      <div className="mb-4">
        <div className="label-overline mb-2">Visible muscles on this view</div>
        <div className="flex flex-wrap gap-1.5">
          {visibleMuscles.map((m) => {
            const on = insightSelection.includes(m.id);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => toggleInsightMuscle(m.id)}
                data-testid={`${testIdPrefix}-insights-pick-${m.id}`}
                className={`min-h-[32px] px-3 rounded-full text-xs font-medium border transition-colors ${
                  on
                    ? "bg-purple-100 text-purple-900 border-purple-300"
                    : "bg-white text-ink-700 border-paper-200 hover:border-purple-300"
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Optional question */}
      <div className="mb-4">
        <label className="label-overline block mb-2">Optional follow-up question</label>
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          data-testid={`${testIdPrefix}-insights-question`}
          placeholder="Why is the right side worse than the left?"
          className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
        />
      </div>

      {error && (
        <div className="mb-4 rounded-2xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-900 flex gap-2" data-testid={`${testIdPrefix}-insights-error`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        <button
          type="button"
          onClick={() => run(false)}
          disabled={busy || insightSelection.length === 0 || !clientId}
          data-testid={`${testIdPrefix}-insights-generate-btn`}
          className="min-h-[44px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 transition-all disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? "Reasoning…" : insights ? "Re-run" : "Generate insights"}
        </button>
        {insights && (
          <button
            type="button"
            onClick={() => run(true)}
            disabled={busy}
            data-testid={`${testIdPrefix}-insights-regen-btn`}
            className="min-h-[44px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 font-medium inline-flex items-center gap-2 transition-all disabled:opacity-50 text-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Regenerate
          </button>
        )}
      </div>

      {insights && (
        <InsightsResult insights={insights} testIdPrefix={testIdPrefix} />
      )}
    </div>
  );
}

function InsightsResult({ insights, testIdPrefix }) {
  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-insights-result`}>
      <div className="rounded-2xl bg-paper-50 border border-paper-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="label-overline">Reasoning</div>
          <span className={`ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
            insights.confidence === "high" ? "bg-sage-100 text-sage-800" :
            insights.confidence === "medium" ? "bg-amber-100 text-amber-800" :
            "bg-rose-100 text-rose-800"
          }`}>{insights.confidence} confidence</span>
        </div>
        <p className="text-sm text-ink-900 leading-relaxed" data-testid={`${testIdPrefix}-insights-reasoning`}>
          {insights.reasoning}
        </p>
      </div>

      <InsightSection title="Compensation patterns" tone="amber" items={insights.compensation_patterns} testid={`${testIdPrefix}-insights-compensation`} />
      <InsightSection title="Related symptoms to ask about" tone="rose" items={insights.related_symptoms} testid={`${testIdPrefix}-insights-symptoms`} />
      <InsightSection title="Evaluate next" tone="sage" items={insights.evaluate_next} testid={`${testIdPrefix}-insights-evaluate`} />
      <InsightSection title="Treatment considerations" tone="ink" items={insights.treatment_considerations} testid={`${testIdPrefix}-insights-treatment`} />
      {insights.cautions?.length > 0 && (
        <InsightSection title="Cautions" tone="rose" items={insights.cautions} testid={`${testIdPrefix}-insights-cautions`} caution />
      )}
    </div>
  );
}

function InsightSection({ title, tone, items, testid, caution }) {
  if (!items || items.length === 0) return null;
  const toneClass = {
    sage: "border-sage-200 bg-sage-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    rose: "border-rose-200 bg-rose-50/40",
    ink: "border-paper-200 bg-paper-50",
  }[tone] || "border-paper-200 bg-paper-50";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`} data-testid={testid}>
      <div className="label-overline mb-2 flex items-center gap-1.5">
        {caution && <AlertTriangle className="w-3.5 h-3.5 text-rose-700" />}
        {title}
      </div>
      <ul className="space-y-1.5 list-disc list-inside text-sm text-ink-900">
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}
